import React from 'react'
import { Box, Typography, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import LearningCenterContentCard from '../../Card/LearningCenterContentCard'
import { FileType } from '../../Elements/FileTypeResource'
import ViewMoreButton from '../../Buttons/ViewMoreButton'
import { ContentAsset } from '../../../swagger'
import { isFeaturedLearningPathAsset } from '../utils/learningCenterUtils'

export interface SearchResultsSectionProps {
  assets: ContentAsset[]
  selectedTabKey: string
  maxItemsToDisplay: number
  isChallengeProgram: boolean
  onViewMore: () => void
  updateFavorite: (assetKey: number, update: Partial<ContentAsset>) => void
}

const SearchResultsSection: React.FC<SearchResultsSectionProps> = ({
  assets,
  selectedTabKey,
  maxItemsToDisplay,
  isChallengeProgram,
  onViewMore,
  updateFavorite,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()

  return (
    <>
      <Typography
        style={{
          color: theme.palette.primary.main,
          marginBottom: theme.spacing(4),
          marginTop: theme.spacing(1.75),
        }}
        variant="h6"
        component="h2"
      >
        {assets.length === 1
          ? `${assets.length} ${t(
              'LearningCenter.SearchResult.ItemFoundIn',
              'item found in'
            )} ${selectedTabKey}`
          : `${assets.length} ${t(
              'LearningCenter.SearchResult.ItemsFoundIn',
              'items found in'
            )} ${selectedTabKey}`}
      </Typography>

      <Box
        display="flex"
        flexWrap="wrap"
        pt={1}
        pb={3}
        justifyContent={{ xs: 'center', sm: 'flex-start' }}
      >
        {assets.slice(0, maxItemsToDisplay).map((asset) => {
          const cycleOrSemesterLabel = isChallengeProgram
            ? `Semester ${asset.semester}`
            : `Cycle ${asset.cycle}`

          const isFeatured = isFeaturedLearningPathAsset({
            learningPathTitle: asset.learningPathTitle,
            listingPriority: asset.listingPriority,
          })

          return (
            <LearningCenterContentCard
              isNdaRequired={asset.isNdaRequired}
              ndaAcknowledged={asset.ndaAcknowledged}
              key={asset.assetKey}
              id={asset.assetKey}
              fileType={
                isFeatured
                  ? FileType.LearningPath
                  : (asset.filetype as FileType)
              }
              title={
                isFeatured
                  ? asset.learningPathTitle ?? ''
                  : asset.title ??
                    t('LearningCenter.EmptyTitle', 'No title available')
              }
              description={
                isFeatured && asset.learningPathDescription
                  ? asset.learningPathDescription
                  : asset.description ??
                    t(
                      'LearningCenter.EmptyDescription',
                      'No description available'
                    )
              }
              isLearningPath={isFeatured}
              subTitle={`${cycleOrSemesterLabel}, Week ${asset.week}`}
              strand={asset.strand ?? ''}
              isFavorite={asset.isFavorited ?? false}
              viewOnly={asset.viewOnly ?? false}
              handleFavoriteButtonClicked={() =>
                updateFavorite(asset.assetKey, {
                  isFavorited: !asset.isFavorited,
                })
              }
            />
          )
        })}
      </Box>

      {assets.length > maxItemsToDisplay && (
        <ViewMoreButton handleViewMoreClick={onViewMore} />
      )}
    </>
  )
}

export default SearchResultsSection
